import React, { useState, useEffect } from 'react';
import sharepointService from '../services/sharepointService';

/**
 * Modal component for creating a new SharePoint client
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {function} onClientCreated - Callback function when client is created successfully
 */
export default function CreateClientModal({
    isOpen,
    onClose,
    onClientCreated
}) {
    const [clientName, setClientName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setClientName('');
            setError('');
            setIsCreating(false);
        }
    }, [isOpen]);

    const getAlphabeticalFolder = (name) => {
        if (!name) return '';
        const firstLetter = name[0].toUpperCase();
        if ('A' <= firstLetter && firstLetter <= 'F') {
            return 'Client Documents/Clients A - F';
        } else if ('G' <= firstLetter && firstLetter <= 'K') {
            return 'Client Documents/Clients G - K';
        } else if ('L' <= firstLetter && firstLetter <= 'Q') {
            return 'Client Documents/Clients L - Q';
        } else if ('R' <= firstLetter && firstLetter <= 'Z') {
            return 'Client Documents/Clients R - Z';
        } else {
            return 'Client Documents/Clients A - F';
        }
    };

    const handleCreateClient = async () => {
        const trimmedClientName = clientName.trim();
        
        if (!trimmedClientName) {
            setError('Client name is required');
            return;
        }

        // Basic validation for client name
        const invalidChars = ['<', '>', ':', '"', '/', '\\', '|', '?', '*'];
        const hasInvalidChars = invalidChars.some(char => trimmedClientName.includes(char));
        
        if (hasInvalidChars) {
            setError(`Client name cannot contain the following characters: ${invalidChars.join(' ')}`);
            return;
        }

        setIsCreating(true);
        setError('');

        try {
            const response = await sharepointService.createClient(trimmedClientName);

            if (response.success) {
                // Notify parent component about the successful client creation
                if (onClientCreated) {
                    onClientCreated(response.client);
                }
                onClose();
            } else {
                // Handle specific error types
                if (response.error === 'CLIENT_EXISTS') {
                    setError(`A client named "${trimmedClientName}" already exists.`);
                } else {
                    setError(response.message || response.error || 'Failed to create client');
                }
            }
        } catch (err) {
            console.error('Error creating client:', err);
            
            // Parse error response
            let errorMessage = 'Failed to create client';
            if (err.response?.data) {
                const errorData = err.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setIsCreating(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleCreateClient();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCreateClient();
        }
    };

    if (!isOpen) return null;

    const targetFolder = getAlphabeticalFolder(clientName);

    return (
        <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="bi bi-building-add me-2 text-success"></i>
                                Create New SharePoint Client
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                disabled={isCreating}
                            ></button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <strong>Create a new client folder in SharePoint</strong>
                                    <div className="mt-1">
                                        <small>The client folder will be automatically created in the appropriate alphabetical location.</small>
                                    </div>
                                </div>

                                {error && (
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label htmlFor="clientName" className="form-label">
                                        <strong>Client Name:</strong>
                                    </label>
                                    <input
                                        type="text"
                                        id="clientName"
                                        className="form-control"
                                        value={clientName}
                                        onChange={(e) => {
                                            setClientName(e.target.value);
                                            setError(''); // Clear error when user starts typing
                                        }}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter client name..."
                                        disabled={isCreating}
                                        autoFocus
                                        maxLength={255}
                                    />
                                    <div className="form-text">
                                        Client name cannot contain: &lt; &gt; : " / \ | ? *
                                    </div>
                                </div>

                                {clientName.trim() && (
                                    <div className="alert alert-secondary">
                                        <i className="bi bi-info-circle me-2"></i>
                                        <strong>Target Location:</strong> {targetFolder}
                                    </div>
                                )}

                                <div className="alert alert-warning">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Note:</strong> The client folder will be created in SharePoint and will be available for document uploads. 
                                    Make sure the client name is spelled correctly before creating.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    disabled={isCreating}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={!clientName.trim() || isCreating}
                                >
                                    {isCreating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Creating Client...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-building-add me-2"></i>
                                            Create Client
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
}
